"use client";

import { c as _c } from "react/compiler-runtime";
import Download from "@/components/download/Download";
import { buildTooltipId, TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { Tooltip } from "react-tooltip";
export type AdditionalDetailsMediaRow = {
  readonly label: string;
  readonly title: string;
  readonly url: string;
  readonly openLabel: string;
  readonly extension?: string | undefined;
  readonly downloadName?: string | undefined;
};
const SAFE_EXTERNAL_PROTOCOLS = new Set(["http:", "https:", "ipfs:", "ar:", "arweave:"]);
const SAFE_DOWNLOAD_PROTOCOLS = new Set(["http:", "https:", "blob:"]);
function getSafeUrl(url: string, allowedProtocols: ReadonlySet<string>): string | null {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }
  try {
    const parsedUrl = new URL(trimmedUrl);
    return allowedProtocols.has(parsedUrl.protocol) ? trimmedUrl : null;
  } catch {
    return null;
  }
}
export function getSafeExternalUrl(url: string): string | null {
  return getSafeUrl(url, SAFE_EXTERNAL_PROTOCOLS);
}
function getSafeDownloadUrl(url: string): string | null {
  return getSafeUrl(url, SAFE_DOWNLOAD_PROTOCOLS);
}
export function AdditionalDetailsSection(t0) {
  const $ = _c(11);
  const {
    title,
    icon,
    children
  } = t0;
  const Icon = icon;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h3 className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-leading-4 tw-text-iron-400">{title}</h3>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-h-px tw-min-w-10 tw-flex-grow tw-bg-gradient-to-r tw-from-iron-700 tw-to-transparent" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">{t1}{t2}{t3}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== t4) {
    t5 = <section className="tw-space-y-6">{t4}{children}</section>;
    $[8] = children;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export function ArweaveLinkRow(t0) {
  const $ = _c(26);
  const {
    row
  } = t0;
  let t1;
  if ($[0] !== row.url) {
    t1 = getSafeExternalUrl(row.url);
    $[0] = row.url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const safeExternalUrl = t1;
  let t2;
  if ($[2] !== row.url) {
    t2 = getSafeDownloadUrl(row.url);
    $[2] = row.url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const safeDownloadUrl = t2;
  let t3;
  if ($[4] !== row.label) {
    t3 = <div className="tw-text-sm tw-font-semibold tw-uppercase tw-leading-5 tw-text-iron-500">{row.label}</div>;
    $[4] = row.label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== row.title) {
    t4 = <div className="tw-min-w-0 tw-text-base tw-font-medium tw-leading-6 tw-text-iron-100 md:tw-truncate">{row.title}</div>;
    $[6] = row.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== row.url || $[9] !== safeExternalUrl) {
    t5 = safeExternalUrl ? <Link href={safeExternalUrl} target="_blank" rel="noopener noreferrer" title={row.url} className="tw-block tw-min-w-0 tw-max-w-full tw-break-all tw-font-mono tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-400 tw-no-underline hover:tw-text-iron-100 md:tw-truncate md:tw-break-normal">{row.url}</Link> : <span title={row.url} className="tw-block tw-min-w-0 tw-max-w-full tw-break-all tw-font-mono tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-500 md:tw-truncate md:tw-break-normal">{row.url}</span>;
    $[8] = row.url;
    $[9] = safeExternalUrl;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== row.downloadName || $[12] !== row.extension || $[13] !== safeDownloadUrl) {
    t6 = row.downloadName && safeDownloadUrl && <Download href={safeDownloadUrl} name={row.downloadName} extension={row.extension ?? ""} variant="text" alwaysShowText={true} />;
    $[11] = row.downloadName;
    $[12] = row.extension;
    $[13] = safeDownloadUrl;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== row.openLabel || $[16] !== safeExternalUrl) {
    t7 = safeExternalUrl && <Link href={safeExternalUrl} target="_blank" rel="noopener noreferrer" aria-label={row.openLabel} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-iron-500 tw-no-underline tw-transition-colors tw-duration-300 desktop-hover:hover:tw-text-iron-100">Open<ArrowTopRightOnSquareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></Link>;
    $[15] = row.openLabel;
    $[16] = safeExternalUrl;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="tw-flex tw-w-full tw-items-center tw-justify-start tw-gap-4 md:tw-w-auto md:tw-justify-end">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t8) {
    t9 = <div className="tw-grid tw-min-w-0 tw-grid-cols-1 tw-gap-2 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-py-3 last:tw-border-b-0 md:tw-grid-cols-[4rem_minmax(10rem,16rem)_minmax(0,1fr)_auto] md:tw-items-center md:tw-gap-6">{t3}{t4}{t5}{t8}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
export function MetadataCard(t0) {
  const $ = _c(17);
  const {
    label,
    value
  } = t0;
  const displayValue = String(value);
  let t1;
  if ($[0] !== displayValue || $[1] !== label) {
    t1 = buildTooltipId("meme-property", label, displayValue);
    $[0] = displayValue;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tooltipId = t1;
  let t2;
  if ($[3] !== label) {
    t2 = <span className="tw-mb-1 tw-block tw-truncate tw-text-xs tw-font-normal tw-uppercase tw-leading-4 tw-tracking-wide tw-text-iron-500">{label}</span>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== displayValue || $[6] !== tooltipId) {
    t3 = <span className="tw-truncate tw-text-xs tw-font-medium tw-leading-4 tw-text-iron-200" data-tooltip-id={tooltipId}>{displayValue}</span>;
    $[5] = displayValue;
    $[6] = tooltipId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== displayValue) {
    t4 = <span className="tw-text-xs">{displayValue}</span>;
    $[8] = displayValue;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4 || $[11] !== tooltipId) {
    t5 = <Tooltip id={tooltipId} place="top" offset={8} opacity={1} style={TOOLTIP_STYLES}>{t4}</Tooltip>;
    $[10] = t4;
    $[11] = tooltipId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = <div className="tw-flex tw-min-w-24 tw-flex-1 tw-flex-col tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-3 tw-py-2">{t2}{t3}{t5}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
export function MetricBlock(t0) {
  const $ = _c(11);
  const {
    label,
    value,
    highlightedLabel: t1
  } = t0;
  const highlightedLabel = t1 === undefined ? false : t1;
  const t2 = `tw-mb-1 tw-text-sm tw-font-medium tw-leading-5 md:tw-mb-2 ${highlightedLabel ? "tw-text-primary-400" : "tw-text-iron-400"}`;
  let t3;
  if ($[0] !== highlightedLabel) {
    t3 = highlightedLabel && <span className="tw-mr-2 tw-inline-block tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-400" />;
    $[0] = highlightedLabel;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label || $[3] !== t2 || $[4] !== t3) {
    t4 = <div className={t2}>{t3}{label}</div>;
    $[2] = label;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== value) {
    t5 = <div className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-white md:tw-text-lg md:tw-leading-6">{value}</div>;
    $[6] = value;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function EmptyDetailsState(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-500">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}